import { Combobox } from './Combobox'
import { useOptionalT } from '../i18n/LocaleProvider'

export interface DirectoryFilterOption {
  /**
   * What this option selects — and what the consumer puts in the query string.
   *
   * **Never translated.** Partners' board keeps its state in the URL so a filtered
   * board can be sent to somebody, and a value that changed with the reader's language
   * would be a link that resolved for its sender and nobody else.
   */
  value: string
  /**
   * What to SHOW instead of the value.
   *
   * Optional, and the fallback is the value itself, which is what every caller did before
   * this existed and is still right for a board whose values are already words a reader
   * understands. The Partners passes a translated label here; the garage and the resort
   * pass none.
   */
  label?: string
  /** How many entries match this option, shown beside the label. */
  count: number
}

export interface DirectoryFilterGroup {
  /** Stable key the consumer uses to track selections, e.g. `job`. */
  id: string
  title: string
  options: DirectoryFilterOption[]
}

interface DirectoryFiltersBase {
  /** Free-text query. Controlled — the consumer owns the value. */
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  groups: DirectoryFilterGroup[]
  /** Selected values per group id. A group missing from the map has nothing selected. */
  selected: Record<string, string[]>
  onClear: () => void
  /** Shown above the groups, e.g. "4 of 6 businesses". */
  resultSummary: string
  clearLabel?: string
}

/**
 * Two shapes, and the callback is what tells them apart — which is the point of the union
 * rather than two optional handlers. A `compact` rail with an `onToggle` would compile and
 * then silently do nothing when a select changed.
 *
 * **Both are multi-select per group.** The compact variant was briefly single-select,
 * because that is all a native `<select>` affords — and it was the wrong trade. It now uses
 * `Combobox`, which does `multiple` properly, so a group can hold several values in either
 * variant and the two differ only in how much room they take.
 */
export type DirectoryFiltersProps =
  | (DirectoryFiltersBase & {
      variant?: 'checkbox'
      /** Adds or removes one value from a group. */
      onToggle: (groupId: string, value: string) => void
      onValues?: never
      comboLabels?: never
    })
  | (DirectoryFiltersBase & {
      variant: 'compact'
      /** The group's whole selection, after the change. Several values are allowed. */
      onValues: (groupId: string, values: string[]) => void
      /**
       * The combobox's own words, already translated. Required for the same reason
       * `Combobox` requires them: an English default here renders English in a page that
       * asked for another language, and nothing fails. See `Combobox`.
       */
      comboLabels: { any: string; filter: string; noMatch: string; clear: string }
      onToggle?: never
    })

/**
 * A `.ul-directory-filters` panel — the left rail of a filterable listing.
 *
 * Fully controlled and content-agnostic: it knows nothing about businesses or jobs, only
 * about groups of options and a query string. The consumer owns the state and does the
 * filtering, which keeps the same panel usable for any listing that needs one.
 *
 * **Two variants.** `checkbox` is the original: every option visible, several selectable per
 * group. It is right where the groups are few and short — `app_garage`'s three programmes,
 * the resort's eleven bookables — because seeing the options *is* the interface.
 *
 * `compact` renders one `<select>` per group. `app_partners` uses it because its five
 * groups carry twenty-six options between them, and a rail listing all of them was taller
 * than the results beside it: on a classifieds board the listings are the page and the
 * filters are a control on it, not the other way round.
 */
export function DirectoryFilters(props: DirectoryFiltersProps) {
  const {
    search,
    onSearchChange,
    searchPlaceholder: searchPlaceholderProp,
    groups,
    selected,
    onClear,
    resultSummary,
    clearLabel: clearLabelProp,
    variant = 'checkbox',
  } = props
  const t = useOptionalT()
  const searchPlaceholder = searchPlaceholderProp ?? t('ul.filters.searchPlaceholder')
  const clearLabel = clearLabelProp ?? t('ul.filters.clear')
  const hasSelection = Object.values(selected).some((values) => values.length > 0) || search.trim() !== ''

  return (
    <aside className="ul-directory-filters">
      <div className="ul-directory-filters-head">
        <h3 className="ul-directory-filters-title">{t('ul.filters.title')}</h3>
        {hasSelection && (
          <button type="button" className="ul-directory-filters-clear" onClick={onClear}>
            {clearLabel}
          </button>
        )}
      </div>

      {/* Not a <form>: there is nothing to submit, the listing filters as you type. */}
      <div className="ul-directory-filters-search">
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
        <i className="flaticon-search" />
      </div>

      <p className="ul-directory-filters-summary">{resultSummary}</p>

      {variant === 'compact'
        ? groups.map((group) => (
            <div className="ul-directory-filters-group ul-directory-filters-group--compact" key={group.id}>
              {/* One `Combobox` per group: several values, a filter field once the list is
                  long enough to want one, and the count beside each option — none of which
                  a native `<select>` can do. */}
              <Combobox
                label={group.title}
                options={group.options.map((o) => ({ value: o.value, label: o.label, count: o.count }))}
                value={selected[group.id] ?? []}
                onChange={(values) => props.onValues?.(group.id, values)}
                multiple
                emptyLabel={props.comboLabels?.any ?? ''}
                searchPlaceholder={props.comboLabels?.filter ?? ''}
                noMatchLabel={props.comboLabels?.noMatch ?? ''}
                clearLabel={props.comboLabels?.clear ?? ''}
              />
            </div>
          ))
        : groups.map((group) => (
        <div className="ul-directory-filters-group" key={group.id}>
          <h4 className="ul-directory-filters-group-title">{group.title}</h4>
          <ul className="ul-directory-filters-options">
            {group.options.map((option) => {
              const id = `filter-${group.id}-${option.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
              const checked = (selected[group.id] ?? []).includes(option.value)
              return (
                <li key={option.value}>
                  {/* Deliberately NOT `.ul-checkbox` — that class is the footer's styled
                      span, a different structure entirely. */}
                  <div className="ul-directory-check">
                    <input
                      type="checkbox"
                      id={id}
                      checked={checked}
                      onChange={() => props.onToggle?.(group.id, option.value)}
                    />
                    <label htmlFor={id}>
                      <span className="label">{option.label ?? option.value}</span>
                      <span className="count">{option.count}</span>
                    </label>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </aside>
  )
}
